import { useMemo, useState, type CSSProperties } from "react";
import type { FkRule } from "../../hooks/use-wrapped-apis";
import {
  discoverPaths,
  detectCollectionPath,
  sampleItem,
  getByPath,
  previewFields,
} from "./mapping-utils";

/**
 * Sprint 16c (S16C.28) — paste a sample response, see the materialised entity
 * preview before saving. Discovers response paths (fed back into the field-map
 * datalist), auto-detects a collection wrapper, and renders the field-map output
 * for the first item plus which FK rules would attempt resolution.
 */
export function TestMappingDrawer({
  fieldMap,
  fkRules,
  collectionPath,
  onPathsDiscovered,
  onCollectionDetected,
}: {
  fieldMap: Record<string, string>;
  fkRules: FkRule[];
  collectionPath: string | null;
  onPathsDiscovered: (paths: string[]) => void;
  onCollectionDetected: (path: string | null) => void;
}) {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<unknown>(null);

  function apply() {
    setError(null);
    if (!text.trim()) {
      setParsed(null);
      return;
    }
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid JSON");
      setParsed(null);
      return;
    }
    setParsed(json);
    const detected = detectCollectionPath(json);
    const item = sampleItem(json, detected ?? collectionPath);
    onPathsDiscovered(discoverPaths(item));
    if (detected && detected !== collectionPath) onCollectionDetected(detected);
  }

  const item = useMemo(() => (parsed ? sampleItem(parsed, collectionPath) : null), [parsed, collectionPath]);
  const preview = useMemo(() => (item ? previewFields(item, fieldMap) : null), [item, fieldMap]);

  return (
    <div style={panel}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 6 }}>
        Test with a sample response
      </div>
      <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginBottom: 8, lineHeight: 1.5 }}>
        Paste one real (or representative) JSON response. Field paths populate the picker, and the preview shows the
        row that would materialise.
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='{ "tickets": [ { "id": 42, "subject": "Login broken", "requester": { "email": "a@acme.com" } } ] }'
        style={textarea}
        rows={6}
      />
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
        <button type="button" onClick={apply} style={applyBtn}>Apply sample</button>
        {error && <span style={{ fontSize: 11, color: "var(--c-red-600)" }}>{error}</span>}
        {parsed != null && !error && (
          <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
            Parsed{collectionPath ? ` · collection "${collectionPath}"` : ""}
          </span>
        )}
      </div>

      {preview && (
        <div style={previewBox}>
          <div style={previewHead}>Materialised entity preview</div>
          {Object.keys(preview).length === 0 ? (
            <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
              No fields matched — check the response paths in the field map.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {Object.entries(preview).map(([k, v]) => (
                  <tr key={k}>
                    <td style={previewKey}>{k}</td>
                    <td style={previewVal}>{String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {fkRules.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={previewHead}>FK resolution</div>
              {fkRules.map((r, i) => {
                const v = item ? getByPath(item, r.wrapped_field) : undefined;
                return (
                  <div key={i} style={{ fontSize: 11, color: "var(--color-text-secondary)", padding: "1px 0" }}>
                    <code style={{ fontFamily: "var(--font-mono)" }}>{r.fk_field || "?"}</code> ← match{" "}
                    <code style={{ fontFamily: "var(--font-mono)" }}>{r.target_entity_type || "?"}.{r.matches_entity_field || "?"}</code>{" "}
                    = {v != null ? <strong>{String(v)}</strong> : <span style={{ color: "var(--color-text-tertiary)" }}>(no value in sample)</span>}
                    <span style={{ color: "var(--color-text-tertiary)" }}> — resolved live on call</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const panel: CSSProperties = {
  marginTop: 12,
  padding: "12px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
};
const textarea: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  resize: "vertical",
};
const applyBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 12px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const previewBox: CSSProperties = {
  marginTop: 10,
  padding: "10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
};
const previewHead: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 6,
};
const previewKey: CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  color: "var(--color-text-tertiary)",
  padding: "2px 8px 2px 0",
  verticalAlign: "top",
  whiteSpace: "nowrap",
};
const previewVal: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-primary)",
  padding: "2px 0",
  wordBreak: "break-word",
};
